import { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import { addDays, addMonths, compareAsc,
  startOfMonth, endOfMonth,
} from "date-fns";
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box, Typography, 
  CircularProgress, Tooltip,  
  FormControl, MenuItem, InputLabel, Select,      
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from "state/axios";
import { 
  parseUTCDate,
  filterCrewMember 
} from "state/helpers";
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import NumberControl from 'components/NumberControl';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RouteList from "./RouteList";
import CrewBlockHours from "./CrewBlockHours";
// import RouteTree from "./RouteTree";


const AircraftRoutes = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  

  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [view, setView] = useState('aircraftRoutes');
  const [offSet, setOffSet] = useState(0);
  const [aircraftRoutes, setAircraftRoutes] = useState([]);
    
  const VIEW_OPTIONS = [
    { value: 'aircraftRoutes', label: 'Aircraft Routes' },
    { value: 'crewBlockHours', label: 'Crew Block Hours' },
  ];
    
  const handleAircraftRoutes = useCallback(async () => {
    if (!startDate || !endDate) return null; 
    
    try {
      setError(null);
      setLoading(true);

      const startMonth = parseUTCDate(startOfMonth(startDate));
      const endMonth = parseUTCDate(endOfMonth(startDate));
      const prev = Math.min(offSet, 0);
      const next = Math.max(offSet, 0);

      let fromDate = parseUTCDate(addMonths(startMonth, prev));
      let toDate = parseUTCDate(endOfMonth(addMonths(startMonth, next)));

      if (
        startDate.getTime() !== startMonth.getTime() ||
        endDate.getTime() !== endMonth.getTime()
      ) {
        fromDate = startDate;
        toDate = endDate;
      }

      const url = `/aircraftroutes`;
      const payload = { 
        startDate: addDays(fromDate, -1), 
        endDate: addDays(toDate, 1) 
      };
      const { data } = await api.post(url, payload);
      const { aircraftRoutes } = data;

      const filtered = aircraftRoutes?.filter(route => {
        const routeDateToCompare = timeMode === 'UTC' 
          ? route.routeDate 
          : route.routeDateLocalBase;
        
        return (
          compareAsc(routeDateToCompare, fromDate) >= 0 &&
          compareAsc(routeDateToCompare, toDate) <= 0
        );
      });    
      
      setAircraftRoutes(filtered);   
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to fetch aircraft routes!");
    } finally {
      setLoading(false);
    }
  }, [timeMode, startDate, endDate, offSet]);

  const crewBlockHours = useMemo(() => {
    if (!aircraftRoutes?.length) return [];

    const crewMap = new Map();

    for (const route of aircraftRoutes) {
      for (const sector of route.sectors || []) {
        const bh = sector?.blockHours || 0;

        for (const cm of sector.crewMembers || []) {
          if (!crewMap.has(cm.crewCode)) {
            crewMap.set(cm.crewCode, {
              _id: cm.crewCode,
              crewCode: cm.crewCode,
              crewName: cm?.crewName || '',
              post: cm?.post || '',
              base: cm?.base || '',
              gender: cm?.gender || '',
              quals: cm?.quals || '',
              trainingQuals: cm?.trainingQuals || [],
              rankPriority: cm?.rankPriority || 99,
              blockHours: bh,
            });
          } else {
            crewMap.get(cm.crewCode).blockHours += bh;
          }
        }
      }
    }

    return Array.from(crewMap.values())
      .sort((a, b) => a.rankPriority - b.rankPriority);
  }, [aircraftRoutes]);

  const filteredCrewBlockHours = useMemo(() => {  
    if (!Array.isArray(crewBlockHours)) return [];
    if (!filters) return crewBlockHours;
    return crewBlockHours?.filter(member => filterCrewMember(member, filters));
  }, [crewBlockHours, filters]);
    

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const {
    // genderOptions,
    baseOptions,
    postOptions,
    trainingQualOptions,
    isActiveOptions,
  } = useMemo(() => {
    // const genders = new Set();
    const bases = new Set();
    const posts = new Set();
    const trainingQuals = new Set();
    const isActives = new Set();

    for (const m of crewBlockHours || []) {
      // if (m?.gender) genders.add(m.gender);
      if (m?.base) bases.add(m.base);
      if (m?.post) posts.add(m.post);
      if (typeof m?.isActive === 'boolean') {
        isActives.add(m.isActive);
      }

      if (Array.isArray(m?.trainingQuals)) {
        for (const q of m.trainingQuals) {
          if (q) trainingQuals.add(q);
        }
      }
    }

    const rankMap = new Map(
      (crewRanksData || []).map(r => [r.rankCode, r])
    );

    const postOptions = [...posts]
      .map(post => rankMap.get(post))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode);

    return {
      // genderOptions: [...genders].sort(),
      baseOptions: [...bases].sort(),
      postOptions,
      trainingQualOptions: [...trainingQuals].sort(),
      isActiveOptions: [...isActives].sort(),
    };
  }, [crewBlockHours, crewRanksData]);
  

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Aircraft Routes" 
          subtitle={loading ? "Loading..." : `List of ${aircraftRoutes?.length || 0} Routes`}
        />
        {!loading && (
          <Box 
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems="flex-end" 
            gap={isMobile ? 1 : 2}
            mb={isMobile ? 1 : 0}
          >
            <Box display="flex" alignItems="center" gap={1}>   
              <Tooltip title={"No. of months"} arrow>
                <span>
                  <NumberControl
                    min={-11}
                    max={11}
                    value={offSet}
                    onChange={(value) => { setOffSet(value) }} 
                    textFieldWidth={45}
                  />
                </span>
              </Tooltip>
              
              <Tooltip title="Fetch data" arrow>
                <span>
                  <StyledButton onClick={handleAircraftRoutes}>
                    <CachedIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}> 
              <FormControl size="small">
                <InputLabel id="view-by-label">View</InputLabel>
                <Select
                  labelId="view-by-label"
                  label="View"
                  value={view}
                  onChange={(e) => setView(e.target.value)}
                  sx={{ width: isMobile ? '9rem' : '11rem' }}
                >
                  {VIEW_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>    

              {view === "crewBlockHours" && (
                <Tooltip title="Filter crew members" arrow>
                  <StyledButton 
                    onClick={handleFilterOpen} 
                  >
                    <FilterAltIcon />
                  </StyledButton>
                </Tooltip>
              )}  
            </Box>
          </Box>
        )}          
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error.message}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {view === "aircraftRoutes" ? (
              <RouteList
                routes={aircraftRoutes || []}
              />
              // <RouteTree routes={aircraftRoutes} />
            ) : (
              <CrewBlockHours
                rows={filteredCrewBlockHours || []}
                offSet={offSet}
              />
            )}            
            
            <ScrollToTop scrollRef={scrollRef} />     
          </Box>
        </Box>
      )}

      <ScrollToTop />
      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          isActiveOptions={isActiveOptions}
        />
      }   
    </Box>
  );
};

export default AircraftRoutes;
